export const guideCategories = [
  "Catastro",
  "Registro y escrituras",
  "Medición",
  "Linderos",
  "GML y georreferenciación",
  "Compraventa",
  "Obra nueva",
  "Antigüedad",
  "Habitabilidad",
  "Eficiencia energética",
  "Estructura",
  "Urbanismo",
];

export const guides = [
  {
    slug: "por-que-catastro-y-escritura-no-coinciden",
    title: "¿Por qué Catastro y la escritura no coinciden?",
    category: "Catastro",
    seoTitle: "Por qué Catastro y escritura tienen superficies distintas | MideFinca",
    metaDescription:
      "Escrituras por cabida aparente, cartografía histórica y segregaciones mal reflejadas: las causas reales de que Catastro y escritura no coincidan, y qué comprobar primero.",
    excerpt: "Las causas reales de la discrepancia más habitual entre Catastro y título, y qué comprobar antes de actuar.",
    h1: "¿Por qué Catastro y la escritura no coinciden?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Dos sistemas que nacieron por caminos distintos",
        paragraphs: [
      "El Registro de la Propiedad documenta derechos: quién es titular y sobre qué finca. Catastro es un inventario fiscal y territorial: dibuja parcelas y les asigna valores. Aunque hoy convergen, durante décadas funcionaron por separado, y las fincas acumulan datos distintos en cada uno.",
      "El resultado es que una misma finca puede tener una superficie en la escritura, otra en Catastro y una tercera en la realidad. Ninguna de las dos primeras es automáticamente la buena.",
        ],
      },
      {
        h2: "Las causas más habituales de la discrepancia",
        paragraphs: ["En nuestro trabajo de campo en Canarias, estas son las causas que aparecen una y otra vez:"],
        list: [
      "Escrituras por cabida aparente: la superficie se declaró de forma aproximada, a veces hace más de un siglo, sin medición.",
      "Cartografía catastral histórica: la primera representación gráfica de muchas zonas se hizo con precisión limitada, y sus errores persisten.",
      "Movimientos de tierra no documentados: compras de franjas, cesiones entre vecinos o cambios de lindero de palabra.",
      "Segregaciones y agrupaciones reflejadas solo en una de las dos fuentes.",
      "Errores de digitalización al incorporar la escritura antigua a sistemas modernos.",
        ],
      },
      {
        h2: "Qué comprobar antes de dar ningún paso",
        paragraphs: [
      "Antes de iniciar cualquier procedimiento hay que saber qué dato es el incorrecto, y eso exige medir. La secuencia razonable es: medición georreferenciada de la finca, comparación con la representación catastral y revisión del historial del título.",
      "Con ese diagnóstico se sabe si la discrepancia está en Catastro, en la escritura o en ambas, y qué cauce puede corresponder. Iniciar trámites sin esa comprobación previa es trabajar a ciegas: a veces la «corrección» agrava el problema.",
        ],
      },
      {
        h2: "¿Qué se puede corregir y quién lo corrige?",
        paragraphs: [
      "La modificación de datos catastrales corresponde a la Gerencia de Catastro, mediante sus procedimientos. La corrección del título o la inscripción de una superficie distinta pasa por notario y Registro. En todos estos expedientes, la prueba técnica de partida es la misma: una medición georreferenciada y, cuando procede, un archivo GML.",
      "Lo que ningún técnico serio puede prometer de antemano es el resultado: cada procedimiento se resuelve según su normativa y sus pruebas. Lo que sí se puede garantizar es que la documentación técnica esté bien medida, bien calculada y bien validada.",
        ],
      },
    ],
    relatedServices: ["correccion-discrepancias-catastrales", "medicion-fincas-terrenos", "gml-catastro-registro"],
    relatedProblems: ["catastro-escritura-no-coinciden", "mi-finca-tiene-mas-o-menos-metros"],
    ogImage: "/og/guia-por-que-catastro-y-escritura-no-coinciden.jpg",
  },
  {
    slug: "que-es-un-archivo-gml",
    title: "¿Qué es un archivo GML y cuándo lo necesitas?",
    category: "GML y georreferenciación",
    seoTitle: "Qué es un archivo GML de una finca y cuándo lo necesitas | MideFinca",
    metaDescription:
      "El GML es la geometría georreferenciada de tu finca que notarías y Registro exigen desde la Ley 13/2015. Qué contiene, cuándo te lo pedirán y por qué debe partir de una medición real.",
    excerpt: "El archivo que notaría y Registro piden desde 2015: qué contiene, cuándo lo exigen y cómo se hace bien.",
    h1: "¿Qué es un archivo GML de una finca?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "La geometría de tu finca, en coordenadas oficiales",
        paragraphs: [
      "GML son las siglas de Geography Markup Language. En la práctica, es un archivo que contiene la geometría de tu finca, sus vértices y su contorno, expresada en coordenadas oficiales del sistema ETRS89. Es la forma estandarizada de decir «esta es mi parcela, exactamente aquí, con exactamente esta forma».",
      "Desde la Ley 13/2015, la representación gráfica georreferenciada de la finca forma parte de muchos expedientes notariales y registrales, y el GML es su formato de intercambio con Catastro.",
        ],
      },
      {
        h2: "Cuándo te lo van a pedir",
        paragraphs: ["Los supuestos más habituales en los que notaría o Registro requieren el GML:"],
        list: [
      "Compraventas, herencias y donaciones en las que se incorpora la representación gráfica.",
      "Segregaciones, divisiones y agrupaciones de parcelas.",
      "Declaraciones de obra nueva y ampliaciones de obra nueva.",
      "Rectificaciones de linderos o de superficie en el título.",
        ],
      },
      {
        h2: "Por qué un GML no se dibuja: se mide",
        paragraphs: [
      "Existen herramientas que permiten generar un GML dibujando el contorno sobre una fotografía aérea. Ese archivo puede ser formalmente válido y estar factualmente mal: si la geometría dibujada no coincide con la realidad medida, se está incorporando al título un dato incorrecto que después habrá que corregir.",
      "El proceso correcto es el inverso: medir la finca sobre el terreno con equipos GNSS, calcular las coordenadas de los vértices, construir la geometría conforme a las especificaciones de Catastro y validar el archivo con las herramientas oficiales antes de entregarlo.",
        ],
      },
      {
        h2: "Qué debe incluir un GML bien elaborado",
        paragraphs: [
      "Vértices con coordenadas medidas, tolerancias conformes a las especificaciones de la Dirección General de Catastro, topología correcta (sin solapes ni huecos espurios) y validación superada en las herramientas oficiales. Si representa construcciones, cada una con su geometría y su posición dentro de la parcela.",
        ],
      },
    ],
    relatedServices: ["gml-catastro-registro", "georreferenciacion-fincas"],
    relatedProblems: ["necesito-gml"],
    ogImage: "/og/guia-que-es-un-archivo-gml.jpg",
  },
  {
    slug: "como-saber-metros-reales-finca",
    title: "¿Cómo saber los metros reales de una finca?",
    category: "Medición",
    seoTitle: "Cómo saber los metros reales de una finca | MideFinca",
    metaDescription:
      "Ni Catastro ni la escritura garantizan la superficie real de tu finca. Te explicamos cómo se mide una parcela con precisión y qué fiabilidad tiene cada fuente de datos.",
    excerpt: "Anuncio, escritura y Catastro pueden decir tres cifras distintas. Así se obtiene la verdadera.",
    h1: "¿Cómo saber los metros reales de una finca?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Tres fuentes de datos, tres niveles de fiabilidad",
        paragraphs: [
      "Cuando preguntas cuántos metros tiene una finca, puedes obtener respuesta de tres sitios: el anuncio o la palabra del propietario, la escritura, y Catastro. Ninguna de las tres garantiza la realidad: el anuncio suele copiar a las otras dos, la escritura puede ser una cabida aparente declarada hace un siglo, y Catastro es una representación cartográfica con su propio margen de error histórico.",
      "La cuarta fuente es la única que mide la realidad: la medición técnica sobre el terreno.",
        ],
      },
      {
        h2: "Cómo se mide una finca con precisión",
        paragraphs: [
      "La medición profesional se realiza con receptores GNSS de precisión, que obtienen coordenadas por satélite. Cuando la cobertura o la vegetación lo exigen, se complementa con estación total. Se toman las coordenadas de todos los vértices de la parcela y de los elementos relevantes de sus límites.",
      "Con esos vértices se calcula la geometría completa: superficie, perímetro y forma. El resultado no es una aproximación: es una medición trazable, expresada en el sistema de coordenadas oficial.",
        ],
      },
      {
        h2: "Qué hacer con el resultado",
        paragraphs: ["Una vez medida la finca, el dato real permite:"],
        list: [
      "Saber qué fuente documental se aleja de la realidad y en cuántos metros.",
      "Negociar una compraventa con datos objetivos.",
      "Decidir si merece la pena iniciar un procedimiento de corrección en Catastro o en el título.",
      "Generar la representación gráfica georreferenciada (GML) si un trámite la requiere.",
        ],
      },
      {
        h2: "¿Puedo medirla yo mismo?",
        paragraphs: [
      "Las aplicaciones de móvil y los visores online dan estimaciones útiles para hacerse una idea, pero su precisión depende del GPS del teléfono y de la ortofoto de base: errores de varios metros por vértice son habituales. Para vender, comprar o acreditar algo ante un tercero, la medición debe ser técnica y trazable.",
        ],
      },
    ],
    relatedServices: ["medicion-fincas-terrenos", "comprobacion-finca-antes-comprar"],
    relatedProblems: ["mi-finca-tiene-mas-o-menos-metros", "comprar-finca-comprobar-superficie"],
    ogImage: "/og/guia-como-saber-metros-reales-finca.jpg",
  },
  {
    slug: "quien-puede-medir-una-finca",
    ogImage: "/og/guia-quien-puede-medir-una-finca.jpg",
    title: "¿Quién puede medir una finca?",
    category: "Medición",
    seoTitle: "¿Quién puede medir una finca y con qué garantías? | MideFinca",
    metaDescription:
      "Qué profesionales pueden medir una finca con validez técnica, qué diferencia una medición profesional de una informal y qué debes exigir al encargarla.",
    excerpt: "No todas las mediciones valen lo mismo: qué profesional puede medir tu finca y qué garantías debe ofrecer.",
    h1: "¿Quién puede medir una finca?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Medir bien exige equipo, método y responsabilidad técnica",
        paragraphs: [
      "Cualquier persona puede estimar la superficie de una finca con una aplicación de móvil o un visor online. Pero cuando la medición va a servir para algo (una compraventa, un expediente en Catastro, un GML para notaría o un conflicto de linderos) hace falta una medición técnica: equipos de precisión, coordenadas en el sistema oficial y un profesional que responda del trabajo.",
      "La diferencia no es solo de precisión (centímetros frente a metros de error), sino de trazabilidad: una medición técnica documenta cómo se midió, con qué equipo y con qué resultado, y puede defenderse ante terceros.",
        ],
      },
      {
        h2: "Qué profesionales realizan este trabajo",
        paragraphs: [
      "En España, la medición de fincas y la documentación técnica asociada la realizan habitualmente profesionales con competencias en medición, cartografía y edificación:",
        ],
        list: [
      "Ingenieros técnicos e ingenieros en geomática y topografía.",
      "Arquitectos y arquitectos técnicos (aparejadores), especialmente cuando la medición incluye edificaciones.",
      "Otros titulados con competencia en trabajos topográficos según el tipo de documento y su finalidad.",
        ],
      },
      {
        h2: "Qué debes exigir al encargar una medición",
        paragraphs: ["Independientemente de la titulación, una medición seria debe incluir:"],
        list: [
      "Trabajo de campo con equipos de precisión (GNSS, estación total), no un dibujo sobre foto aérea.",
      "Coordenadas en el sistema de referencia oficial (ETRS89, huso 28 en Canarias).",
      "Un plano con la geometría medida y una memoria de cómo se obtuvo.",
      "Identificación del técnico responsable del trabajo.",
        ],
      },
      {
        h2: "¿Y si el documento es para Catastro, notaría o Registro?",
        paragraphs: [
      "Cuando el documento se aporta a un expediente, la firma de un técnico competente es requisito habitual, y el contenido debe cumplir las especificaciones del procedimiento correspondiente. Conviene encargarlo a quien conozca esos requisitos de antemano: rehacer un trabajo mal planteado cuesta más que hacerlo bien desde el principio.",
        ],
      },
    ],
    relatedServices: ["medicion-fincas-terrenos", "levantamiento-topografico", "georreferenciacion-fincas"],
    relatedProblems: ["mi-finca-tiene-mas-o-menos-metros"],
  },
  {
    slug: "cuando-necesito-georreferenciar-una-finca",
    ogImage: "/og/guia-cuando-necesito-georreferenciar-una-finca.jpg",
    title: "¿Cuándo necesito georreferenciar una finca?",
    category: "GML y georreferenciación",
    seoTitle: "Cuándo necesitas georreferenciar una finca | MideFinca",
    metaDescription:
      "Compraventas, herencias, segregaciones, obra nueva y correcciones de Catastro: los casos en los que necesitarás la georreferenciación de tu finca y por qué.",
    excerpt: "Los trámites en los que te pedirán la finca georreferenciada y qué significa exactamente.",
    h1: "¿Cuándo necesito georreferenciar una finca?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Qué significa georreferenciar",
        paragraphs: [
      "Georreferenciar una finca es fijar la posición de sus vértices en coordenadas oficiales del sistema ETRS89 (en Canarias, huso UTM 28). A partir de ese momento, la finca deja de describirse con palabras («al norte, tierras de…») y pasa a tener una posición exacta y comprobable sobre la cartografía oficial.",
      "La georreferenciación es la base técnica de la representación gráfica que Catastro y el Registro de la Propiedad utilizan desde la Ley 13/2015.",
        ],
      },
      {
        h2: "Los casos en los que la necesitarás",
        paragraphs: ["Estos son los supuestos más habituales en la práctica notarial y registral:"],
        list: [
      "Compraventas, herencias y donaciones en las que se incorpora la representación gráfica de la finca a la escritura.",
      "Segregaciones, divisiones y agrupaciones de parcelas: la nueva geometría debe definirse en coordenadas.",
      "Declaraciones y ampliaciones de obra nueva, que suelen requerir la representación de la parcela y de la construcción.",
      "Correcciones de discrepancias con Catastro: la prueba técnica es una medición georreferenciada.",
      "Conflictos o dudas de linderos, donde las coordenadas de los vértices aportan objetividad.",
        ],
      },
      {
        h2: "¿Puedo georreferenciarla yo mismo con herramientas online?",
        paragraphs: [
      "Existen herramientas que permiten dibujar la geometría sobre una ortofoto. El problema es que una geometría dibujada a ojo no es una geometría medida: si no coincide con la realidad del terreno, el error queda incorporado a la documentación oficial y corregirlo después es más costoso.",
      "La georreferenciación seria parte de la medición sobre el terreno con equipos GNSS y del cálculo de coordenadas con tolerancias conformes a las especificaciones de Catastro.",
        ],
      },
    ],
    relatedServices: ["georreferenciacion-fincas", "gml-catastro-registro", "medicion-fincas-terrenos"],
    relatedProblems: ["necesito-gml", "catastro-escritura-no-coinciden"],
  },
  {
    slug: "como-saber-donde-estan-los-limites-de-una-parcela",
    ogImage: "/og/guia-como-saber-donde-estan-los-limites-de-una-parcela.jpg",
    title: "¿Cómo saber dónde están los límites de una parcela?",
    category: "Linderos",
    seoTitle: "Cómo saber dónde están los límites de tu parcela | MideFinca",
    metaDescription:
      "Hitos, escritura, Catastro y medición: qué fuentes existen para conocer los límites reales de tu parcela, cuánta fiabilidad tiene cada una y cómo se comprueban.",
    excerpt: "Escritura, Catastro, hitos y vallados: qué fiabilidad tiene cada fuente y cómo se comprueban los límites de verdad.",
    h1: "¿Cómo saber dónde están los límites de una parcela?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Las fuentes disponibles y su fiabilidad real",
        paragraphs: [
      "Para saber dónde empieza y acaba una parcela existen varias fuentes, y conviene conocer su fiabilidad antes de confiar en ellas:",
        ],
        list: [
      "Hitos y mojones: marcas físicas antiguas. Útiles si se conservan, pero muchos han desaparecido o se han movido.",
      "Vallados y muros: reflejan la ocupación actual, no necesariamente el límite documental.",
      "La escritura: describe la cabida y las colindancias, pero las descripciones antiguas pueden ser ambiguas hoy.",
      "Catastro: dibuja la parcela sobre la cartografía, pero su representación es una referencia gráfica con su propio margen de error histórico, no una prueba jurídica de linderos.",
        ],
      },
      {
        h2: "La comprobación técnica: medir y comparar",
        paragraphs: [
      "La forma objetiva de conocer los límites es medir la parcela sobre el terreno, localizar los elementos físicos de lindero existentes y comparar la geometría resultante con la escritura y con Catastro.",
      "Cuando los vértices pueden determinarse, se replantean sobre el terreno y quedan señalizados: sabrás exactamente dónde está cada esquina de tu propiedad.",
        ],
      },
      {
        h2: "Si el límite medido no coincide con lo que hay sobre el terreno",
        paragraphs: [
      "Es frecuente que el vallado actual no coincida con el límite documental. En ese caso la documentación técnica deja constancia de la situación medida y de la discrepancia; el camino para resolverla (acuerdo con el colindante o vía judicial) depende del caso y puede requerir otros profesionales.",
        ],
      },
    ],
    relatedServices: ["linderos-deslindes", "medicion-fincas-terrenos", "georreferenciacion-fincas"],
    relatedProblems: ["no-se-donde-termina-mi-finca"],
  },
  {
    slug: "vecino-no-esta-de-acuerdo-con-el-lindero",
    ogImage: "/og/guia-vecino-no-esta-de-acuerdo-con-el-lindero.jpg",
    title: "¿Qué hacer si un vecino no está de acuerdo con el lindero?",
    category: "Linderos",
    seoTitle: "Vecino en desacuerdo con el lindero: qué hacer | MideFinca",
    metaDescription:
      "Si tu vecino discute el lindero de la finca, lo primero es una medición objetiva. Te explicamos los pasos razonables, del acuerdo con datos a la vía judicial.",
    excerpt: "Antes de discutir, medir: los pasos razonables cuando un vecino discute el límite de la finca.",
    h1: "¿Qué hacer si un vecino no está de acuerdo con el lindero?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Primero, datos: la medición objetiva",
        paragraphs: [
      "La mayoría de los conflictos de linderos se sostienen sobre suposiciones: cada parte cree que el límite está donde recuerda o donde le conviene. El primer paso razonable es sustituir las opiniones por una medición técnica de la finca, comparada con la escritura y con Catastro.",
      "Con un plano medido sobre la mesa, muchas discusiones se enfrían: ambas partes ven el mismo dato.",
        ],
      },
      {
        h2: "El acuerdo con datos es el camino más barato",
        paragraphs: [
      "Si la medición aclara la posición del límite y ambas partes la aceptan, el acuerdo puede documentarse y, si se quiere darle mayor firmeza, formalizarse con intervención notarial. Este escenario suele ser más rápido y mucho más barato que cualquier alternativa contenciosa.",
      "Ni el técnico ni nadie puede obligar al vecino a aceptar un lindero por las buenas: la medición aporta la prueba objetiva, pero la aceptación es voluntaria mientras no haya resolución judicial.",
        ],
      },
      {
        h2: "Si no hay acuerdo: la vía judicial",
        paragraphs: [
      "Cuando el desacuerdo persiste, existe el procedimiento judicial de deslinde. En él, el juzgado designa al perito que determinará el límite: el peritaje judicial no lo elige ninguna de las partes.",
      "La documentación técnica previa (medición, planos y comparativa documental) sigue siendo muy útil como prueba de parte para tu abogado, aunque la determinación final corresponda al perito judicial y al juez.",
        ],
      },
    ],
    relatedServices: ["linderos-deslindes", "medicion-fincas-terrenos"],
    relatedProblems: ["no-se-donde-termina-mi-finca"],
  },
  {
    slug: "que-comprobar-antes-de-comprar-un-terreno",
    ogImage: "/og/guia-que-comprobar-antes-de-comprar-un-terreno.jpg",
    title: "¿Qué comprobar antes de comprar un terreno?",
    category: "Compraventa",
    seoTitle: "Qué comprobar antes de comprar un terreno en Canarias | MideFinca",
    metaDescription:
      "Superficie real, linderos, coherencia entre Catastro y escritura, construcciones declaradas y urbanismo: la lista de comprobaciones antes de comprar un terreno.",
    excerpt: "La lista de comprobaciones técnicas y documentales que evitan sorpresas después de firmar.",
    h1: "¿Qué comprobar antes de comprar un terreno?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Lo que se ve no siempre es lo que se compra",
        paragraphs: [
      "Al comprar un terreno se firma sobre documentos, no sobre lo que parece verse en la visita. El anuncio puede copiar la superficie de Catastro, la escritura puede recoger una cabida de hace un siglo y el vallado puede no estar donde debería. Cada una de esas cifras puede contar una historia distinta.",
        ],
      },
      {
        h2: "Las comprobaciones técnicas esenciales",
        paragraphs: ["Antes de firmar, conviene verificar:"],
        list: [
      "Superficie real medida: cuántos metros tiene de verdad, no cuántos dicen los papeles.",
      "Linderos: dónde están los límites y si el cerramiento actual coincide con la documentación.",
      "Coherencia documental: si realidad, Catastro y escritura cuentan lo mismo o hay discrepancias.",
      "Construcciones existentes: si hay edificaciones y si constan declaradas en escritura y Catastro.",
      "Accesos y servidumbres visibles: caminos de paso, canalizaciones o servidumbres que afecten al terreno.",
        ],
      },
      {
        h2: "Las comprobaciones jurídicas y urbanísticas",
        paragraphs: [
      "La situación registral, las cargas y la calificación urbanística del suelo se revisan con nota simple, Registro y planeamiento municipal; ahí intervienen el notario, tu abogado o la administración. La parte técnica de medición y comprobación física es la que aportamos desde MideFinca.",
      "Si la comprobación detecta discrepancias, tendrás datos para negociar el precio, exigir que el vendedor regularice antes de firmar o descartar la operación a tiempo.",
        ],
      },
    ],
    relatedServices: ["comprobacion-finca-antes-comprar", "medicion-fincas-terrenos", "linderos-deslindes"],
    relatedProblems: ["comprar-finca-comprobar-superficie"],
  },
  {
    slug: "como-acreditar-antiguedad-construccion",
    ogImage: "/og/guia-como-acreditar-antiguedad-construccion.jpg",
    title: "¿Cómo acreditar la antigüedad de una construcción?",
    category: "Antigüedad",
    seoTitle: "Cómo acreditar la antigüedad de una construcción | MideFinca",
    metaDescription:
      "Fotografía aérea histórica, documentación de época y certificado técnico: cómo se acredita desde cuándo existe una construcción y para qué se utiliza.",
    excerpt: "La antigüedad no se declara: se demuestra con evidencias. Qué pruebas sirven y cómo se documentan.",
    h1: "¿Cómo acreditar la antigüedad de una construcción?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "La antigüedad se acredita con evidencias, no con afirmaciones",
        paragraphs: [
      "Saber que una construcción «lleva ahí toda la vida» no basta: para un expediente hace falta demostrarlo. La acreditación seria combina la medición actual del edificio con evidencias históricas que lo muestren ya construido en fechas concretas.",
        ],
      },
      {
        h2: "Qué pruebas suelen utilizarse",
        paragraphs: ["Las evidencias más habituales, de mayor a menor peso técnico:"],
        list: [
      "Fotografía aérea histórica: vuelos antiguos y ortofotos de distintas épocas permiten ver si la construcción ya existía en una fecha dada.",
      "Cartografía y Catastro histórico: representaciones antiguas de la parcela y sus construcciones.",
      "Documentación de época: escrituras, licencias, facturas de obra, contratos de suministro o recibos antiguos.",
      "Fotografías datadas y otros testimonios documentales.",
        ],
      },
      {
        h2: "El certificado técnico de antigüedad",
        paragraphs: [
      "El técnico mide la construcción, analiza las evidencias disponibles y emite un certificado que documenta qué existe hoy y desde cuándo consta que existe según las pruebas. Se utiliza habitualmente en declaraciones de obra nueva, expedientes de regularización y compraventas.",
      "Un certificado serio dice hasta dónde llega la prueba: si las evidencias solo permiten acreditar la existencia desde cierto año, eso es lo que constará. Y los efectos jurídicos o urbanísticos de esa antigüedad dependen de la normativa aplicable y de cada expediente, que corresponde valorar a otros profesionales.",
        ],
      },
    ],
    relatedServices: ["certificado-antiguedad", "declaracion-obra-nueva", "ampliacion-obra-nueva"],
    relatedProblems: ["construccion-no-documentada"],
  },
  {
    slug: "que-es-declaracion-de-obra-nueva",
    ogImage: "/og/guia-que-es-declaracion-de-obra-nueva.jpg",
    title: "¿Qué es una declaración de obra nueva?",
    category: "Obra nueva",
    seoTitle: "Qué es una declaración de obra nueva y cuándo se necesita | MideFinca",
    metaDescription:
      "La declaración de obra nueva es el cauce para documentar construcciones que no figuran en la escritura. Qué es, quién interviene y qué documentación técnica necesita.",
    excerpt: "El cauce habitual para que una construcción existente pase a figurar en tu escritura: qué es y quién interviene.",
    h1: "¿Qué es una declaración de obra nueva?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Documentar lo que existe pero no figura",
        paragraphs: [
      "Muchas fincas tienen construcciones, como viviendas, alpendres o anexos, que no aparecen en la escritura ni en el Registro. La declaración de obra nueva es el instrumento notarial por el que el propietario declara la existencia de esa construcción para que pueda incorporarse a la documentación del inmueble.",
      "Es importante entender qué hace y qué no hace: documenta la realidad construida, pero no equivale por sí sola a una legalización urbanística, que depende del planeamiento municipal y de la normativa aplicable a cada caso.",
        ],
      },
      {
        h2: "Quién interviene en el proceso",
        paragraphs: ["La declaración de obra nueva es un trabajo de equipo entre varios profesionales:"],
        list: [
      "El técnico: mide la construcción, elabora los planos y el certificado descriptivo, y prepara la representación gráfica georreferenciada (GML) cuando el expediente la requiere.",
      "El notario: autoriza la escritura de declaración de obra nueva.",
      "El Registro de la Propiedad: valora la inscripción de lo declarado.",
      "Catastro: tras la escritura, suele presentarse la alteración para que la cartografía y los datos reflejen la construcción.",
        ],
      },
      {
        h2: "Qué documentación técnica se necesita",
        paragraphs: [
      "La base del expediente es técnica: medición de la construcción y de la parcela, planos de la edificación, certificado técnico descriptivo y, en la mayoría de los casos, archivo GML con la geometría georreferenciada. Si la construcción tiene años, un certificado de antigüedad suele ser pieza clave del expediente.",
      "Los requisitos exactos pueden variar según el caso y la antigüedad de la obra; por eso conviene medir y documentar primero, y plantear el expediente después con datos reales.",
        ],
      },
    ],
    relatedServices: ["declaracion-obra-nueva", "certificado-antiguedad", "gml-catastro-registro"],
    relatedProblems: ["construccion-no-documentada"],
  },
  {
    slug: "como-documentar-ampliacion-no-declarada",
    ogImage: "/og/guia-como-documentar-ampliacion-no-declarada.jpg",
    title: "¿Cómo documentar una ampliación no declarada?",
    category: "Obra nueva",
    seoTitle: "Cómo documentar una ampliación no declarada | MideFinca",
    metaDescription:
      "Si tu vivienda tiene más metros de los que figuran en la escritura, la ampliación de obra nueva es el cauce para documentarlos. Pasos, documentación y precauciones.",
    excerpt: "Tu vivienda creció pero la escritura no lo sabe: cómo se documentan esos metros paso a paso.",
    h1: "¿Cómo documentar una ampliación no declarada?",
    updatedAt: "2026-07-15",
    sections: [
      {
        h2: "Una situación mucho más común de lo que parece",
        paragraphs: [
      "Terrazas cerradas, dormitorios añadidos, segundas plantas, anexos… Es muy frecuente que la vivienda real tenga más metros construidos que los que figuran en la escritura y en Catastro. Mientras la ampliación no conste, el inmueble documentado y el real no coinciden, y eso complica ventas, valoraciones, hipotecas y herencias.",
      "El cauce habitual para documentar esos metros es la ampliación de obra nueva: una escritura notarial que declara la superficie construida adicional.",
        ],
      },
      {
        h2: "Los pasos del proceso",
        paragraphs: ["El orden razonable de trabajo es:"],
        list: [
      "Medición completa de la vivienda, incluida la parte ampliada, con plano actualizado y cómputo de superficies.",
      "Certificado técnico descriptivo de la ampliación, con la representación gráfica georreferenciada cuando el expediente la requiere.",
      "Si la ampliación tiene años: acreditación de su antigüedad con evidencias (fotografía aérea histórica, documentos de época).",
      "Escritura de ampliación de obra nueva ante notario y presentación registral, que corresponden a esos profesionales.",
      "Actualización posterior en Catastro con la documentación correspondiente.",
        ],
      },
      {
        h2: "Precauciones antes de empezar",
        paragraphs: [
      "La viabilidad y los requisitos dependen del caso: la antigüedad acreditable, la situación urbanística del suelo y la documentación disponible condicionan el expediente. Documentar una ampliación y legalizarla urbanísticamente son cosas distintas, y conviene saber en cuál de los dos escenarios estás antes de gastar.",
      "Nuestra parte es la técnica: medir, dibujar y certificar la realidad construida para que notario, Registro y demás profesionales trabajen sobre datos reales.",
        ],
      },
    ],
    relatedServices: ["ampliacion-obra-nueva", "certificado-antiguedad", "certificado-habitabilidad"],
    relatedProblems: ["construccion-no-documentada", "mi-finca-tiene-mas-o-menos-metros"],
  },
  {
    slug: "cuando-es-obligatorio-certificado-eficiencia-energetica",
    ogImage: "/og/guia-cuando-es-obligatorio-certificado-eficiencia-energetica.jpg",
    title: "¿Cuándo es obligatorio el certificado de eficiencia energética?",
    category: "Eficiencia energética",
    seoTitle: "Cuándo es obligatorio el certificado de eficiencia energética | MideFinca",
    metaDescription:
      "Venta, alquiler, obra nueva y rehabilitación: cuándo exige la normativa el certificado de eficiencia energética, qué excepciones existen y cuánto dura su validez.",
    excerpt: "Vender, alquilar, obra nueva o rehabilitar: los supuestos en los que la normativa lo exige y sus excepciones.",
    h1: "¿Cuándo es obligatorio el certificado de eficiencia energética?",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "La regla general: vender o alquilar lo exige",
        paragraphs: [
      "Desde 2013, con carácter general, todo inmueble que se vende o se alquila en España debe disponer del certificado de eficiencia energética. Es el documento que califica la vivienda o el local de la A a la G según su consumo de energía y sus emisiones, y que da lugar a la conocida etiqueta energética.",
      "En la práctica lo notarás en tres momentos: el anuncio de venta o alquiler debe mostrar la calificación, el contrato de arrendamiento exige entregar copia al inquilino y, en una compraventa, la notaría pedirá el certificado registrado para la escritura.",
        ],
      },
      {
        h2: "Otros supuestos en los que también se exige",
        paragraphs: ["Además de la venta y el alquiler, la normativa prevé el certificado en otros escenarios:"],
        list: [
      "Obra nueva: el edificio o vivienda recién construida obtiene su certificado al finalizar la obra.",
      "Rehabilitaciones importantes que modifican sustancialmente la envolvente o las instalaciones.",
      "Determinadas ayudas y subvenciones públicas de rehabilitación, que lo requieren como documentación del expediente.",
      "Edificios de uso público con cierta superficie, que además deben exhibir la etiqueta.",
        ],
      },
      {
        h2: "Las excepciones que contempla la norma",
        paragraphs: [
      "La normativa recoge excepciones tasadas. Las más habituales en viviendas e inmuebles de particulares:",
        ],
        list: [
      "Inmuebles aislados con superficie útil inferior a 50 m².",
      "Inmuebles que se compran para su demolición o rehabilitación importante, debidamente acreditado.",
      "Viviendas de uso temporal previsto inferior a cuatro meses al año, con la declaración responsable correspondiente.",
      "Inmuebles protegidos por su valor histórico, cuando las exigencias alterarían su carácter, conforme al informe técnico que lo acredite.",
        ],
      },
      {
        h2: "Cuánto dura y cuándo conviene renovarlo",
        paragraphs: [
      "El certificado tiene una vigencia máxima de diez años. Pasado ese plazo, hay que renovarlo. Y aunque siga vigente, conviene actualizarlo si se han hecho reformas relevantes (ventanas, aislamiento, caldera o aerotermia), porque la calificación puede mejorar y una mejor letra ayuda a vender o alquilar.",
      "El certificado debe estar registrado en el órgano competente de la comunidad autónoma: en Canarias, el registro correspondiente del Gobierno de Canarias. Solo con ese registro la etiqueta es válida para anunciar y firmar.",
        ],
      },
      {
        h2: "Qué pasa si vendes o alquilas sin certificado",
        paragraphs: [
      "No disponer del certificado no invalida la operación, pero sí genera problemas prácticos: el anuncio sería incorrecto, la notaría puede parar la firma hasta tenerlo y la responsabilidad por no facilitarlo recae sobre el propietario. Lo sensato es encargarlo con antelación: la visita es breve y el certificado se obtiene en pocos días.",
        ],
      },
      {
        h2: "Quién puede emitirlo",
        paragraphs: [
      "El certificado lo firman técnicos con la titulación habilitante que exige la normativa. En MideFinca lo elaboramos con visita al inmueble y toma de datos reales, lo calculamos con los procedimientos oficiales y lo registramos en el órgano competente de Canarias, entregándote la etiqueta lista para el anuncio y la firma. Si crees que tu caso puede estar exento, consúltanoslo antes de descartarlo.",
        ],
      },
    ],
    relatedServices: ["certificado-eficiencia-energetica", "certificado-habitabilidad", "certificado-superficies"],
    relatedProblems: [],
  },
  {
    slug: "fisuras-y-grietas-cuando-preocuparse",
    ogImage: "/og/guia-fisuras-y-grietas-cuando-preocuparse.jpg",
    title: "Fisuras y grietas: cuándo preocuparse",
    category: "Estructura",
    seoTitle: "Fisuras y grietas: cuándo preocuparse | MideFinca",
    metaDescription:
      "No toda fisura es grave ni toda grieta es inofensiva. Aprende a distinguir las lesiones habituales de las señales de alarma y cuándo conviene una inspección técnica.",
    excerpt: "No toda fisura es grave ni toda grieta es inofensiva: cómo distinguir lo habitual de las señales de alarma.",
    h1: "Fisuras y grietas: cuándo preocuparse",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "Primero, la terminología: fisura no es lo mismo que grieta",
        paragraphs: [
      "En el lenguaje técnico, la fisura es una abertura fina, normalmente superficial, que no atraviesa el elemento; la grieta es más abierta, más profunda y suele indicar un movimiento o una rotura más seria. La diferencia importa porque la mayoría de las fisuras son un problema de acabado y la mayoría de las grietas merecen, al menos, vigilancia.",
      "Otro matiz útil: no es lo mismo lo que aparece en un tabique de yeso o ladrillo, que no sostiene nada, que lo que aparece en un pilar, una viga, un forjado o un muro de carga, que son los elementos que sí sostienen el edificio.",
        ],
      },
      {
        h2: "Lesiones habituales que suelen ser benignas",
        paragraphs: ["Estas aparecen en casi cualquier edificio y, en general, no afectan a la estructura:"],
        list: [
      "Fisuras finas en jambas y dinteles de ventanas y puertas, donde el muro está más debilitado.",
      "Fisuras en encuentros entre materiales distintos: tabique con pilar, techo con muro, carpintería con tabique.",
      "Fisuración superficial de enfoscados y pinturas por retracción o cambios de temperatura.",
      "Fisuras en tabiques de partición que se repiten tras pintar, sin crecer en anchura.",
        ],
      },
      {
        h2: "Señales de alarma que sí merecen atención",
        paragraphs: [
      "Hay patrones que un técnico toma en serio porque pueden indicar asientos del terreno, sobrecargas, corrosión de armaduras o problemas de la propia estructura:",
        ],
        list: [
      "Grietas que crecen: se alargan o se abren con los meses o con las estaciones.",
      "Grietas diagonales en las esquinas de ventanas y puertas, o inclinadas recorriendo el muro.",
      "Grietas escalonadas que siguen las juntas de la fábrica de ladrillo o bloque.",
      "Fisuras que atraviesan el forjado o aparecen en pilares, vigas y muros de carga.",
      "Desplomes, abombamientos o desprendimientos de hormigón con armadura oxidada a la vista, frecuente en balcones.",
      "Puertas o ventanas que de repente rozan o no cierran, o suelos que se inclinan.",
        ],
      },
      {
        h2: "Qué puedes hacer antes de llamar a un técnico",
        paragraphs: [
      "Documentar bien la lesión ayuda mucho al diagnóstico y no cuesta nada:",
        ],
        list: [
      "Fotografía la lesión con fecha y con una referencia de escala (una regla o una moneda).",
      "Anota cuándo apareció y si coincide con algo: una obra cercana, una reforma, un siniestro, una época de lluvias.",
      "Repite la foto cada dos o tres meses desde el mismo punto para ver si evoluciona.",
      "En grietas, un testigo de yeso o un fisurímetro permite saber si sigue moviéndose; lo coloca un técnico.",
        ],
      },
      {
        h2: "Cuándo conviene una inspección técnica",
        paragraphs: [
      "Pide valoración técnica si la lesión coincide con alguna señal de alarma, si afecta a elementos estructurales, si va a condicionar una compraventa o una reforma, o si simplemente no consigues descartar que sea grave. También si una comunidad, una aseguradora o una administración te requiere un informe.",
      "La inspección resuelve exactamente esa duda: un técnico examina la estructura, describe y documenta las lesiones, valora su alcance y te dice qué hacer: vigilancia, reparación o, cuando proceda, ensayos o un estudio más detallado. Es el contenido del certificado del estado estructural, que puedes consultar en la página del servicio.",
        ],
      },
    ],
    relatedServices: ["certificado-estructural", "certificado-antiguedad", "certificado-habitabilidad"],
    relatedProblems: [],
  },
{
    slug: "como-segregar-una-finca-en-canarias",
    ogImage: "/og/guia-como-segregar-una-finca-en-canarias.jpg",
    title: "Cómo segregar una finca en Canarias",
    category: "Registro y escrituras",
    seoTitle: "Cómo segregar una finca en Canarias: pasos y documentación | MideFinca",
    metaDescription:
      "Qué es una segregación, qué documentación necesita, por qué hay que comprobar la normativa urbanística y qué papel juegan Ayuntamiento, Notaría, Catastro y Registro.",
    excerpt: "Qué es una segregación, qué documentación necesita y por qué hay que comprobar antes la normativa urbanística.",
    h1: "Cómo segregar una finca en Canarias",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "Qué es una segregación",
        paragraphs: [
      "Segregar una finca es dividirla en dos o más parcelas independientes, cada una con su propia geometría, superficie y documentación. Es una operación habitual en herencias, en ventas parciales o cuando una finca resulta demasiado grande para el uso que se le quiere dar.",
      "Lo importante es entender que segregar no es solo dibujar una línea sobre el plano: cada parcela resultante debe quedar técnicamente definida y la operación debe ser posible conforme a la normativa urbanística aplicable.",
        ],
      },
      {
        h2: "Por qué hay que comprobar primero la normativa urbanística",
        paragraphs: [
      "No toda finca puede dividirse. La normativa urbanística de cada municipio establece condiciones como la superficie mínima de las parcelas resultantes, los frentes o los accesos necesarios. Por eso, antes de plantear una segregación, hay que comprobar si la finca la admite y con qué condiciones.",
      "Esta comprobación previa evita gastar en mediciones y documentación para una operación que después no pueda autorizarse o inscribirse.",
        ],
      },
      {
        h2: "La parte técnica: medición, georreferenciación y representación gráfica",
        paragraphs: [
      "Una vez comprobada la viabilidad, la finca se mide y se georreferencia, y se definen las nuevas parcelas: dónde pasan los nuevos linderos, qué superficie tiene cada una y qué coordenadas tienen sus vértices.",
      "Con esa definición se elaboran los planos de cada parcela y, cuando el trámite lo requiere, la representación gráfica georreferenciada en formato GML y el Informe de Validación Gráfica Alternativa positivo, cuando proceda.",
        ],
      },
      {
        h2: "Quién interviene en el procedimiento",
        paragraphs: [
      "Según el caso, intervienen varios organismos, cada uno en su parte:",
        ],
        list: [
      "El Ayuntamiento, cuando la normativa urbanística exige autorización o comunicación para dividir la finca.",
      "La Notaría, para la escritura de la división.",
      "El Registro de la Propiedad, para la inscripción de las fincas resultantes.",
      "Catastro, para la actualización de su representación gráfica a partir de lo documentado.",
        ],
      },
      {
        h2: "Qué documentación se necesita",
        paragraphs: [
      "Para valorar una segregación conviene reunir la escritura de la finca, la referencia catastral y, si se tiene, una nota simple del Registro. Con la medición y la revisión documental se prepara el informe técnico y los planos de las parcelas resultantes, que son la base sobre la que trabajan después la Notaría y el Registro.",
        ],
      },
    ],
    relatedServices: ["segregacion-finca-canarias", "medicion-fincas-terrenos", "correccion-discrepancias-catastrales"],
    relatedProblems: ["dividir-finca-en-parcelas"],
  },
  {
    slug: "como-agrupar-fincas-en-canarias",
    ogImage: "/og/guia-como-agrupar-fincas-en-canarias.jpg",
    title: "Cómo agrupar fincas en Canarias",
    category: "Registro y escrituras",
    seoTitle: "Cómo agrupar varias fincas en una sola en Canarias | MideFinca",
    metaDescription:
      "Qué es una agrupación de fincas, qué hay que comprobar antes y qué documentación técnica se prepara para Notaría, Registro y Catastro.",
    excerpt: "Qué es una agrupación de fincas, qué hay que comprobar antes y qué documentación se prepara.",
    h1: "Cómo agrupar fincas en Canarias",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "Qué es una agrupación de fincas",
        paragraphs: [
      "Agrupar fincas es unir dos o más parcelas en una única finca resultante, con una nueva geometría y una nueva superficie documentadas. Es frecuente tras comprar la finca vecina, en herencias con varias parcelas o cuando interesa gestionar el conjunto como una sola propiedad.",
        ],
      },
      {
        h2: "Qué hay que comprobar antes",
        paragraphs: [
      "No todas las fincas pueden agruparse automáticamente. Antes de plantear la operación hay que comprobar que las parcelas sean colindantes, revisar la titularidad y las cargas registrales de cada una y confirmar que la documentación existente es coherente con la realidad física.",
      "Si alguna de las fincas tiene discrepancias entre escritura, Catastro y realidad, conviene resolverlas o al menos conocerlas antes de agrupar.",
        ],
      },
      {
        h2: "La parte técnica de la agrupación",
        paragraphs: [
      "El trabajo técnico consiste en medir y georreferenciar el conjunto, definir la geometría de la finca resultante y elaborar los planos de la situación anterior y de la finca agrupada. Cuando el trámite lo requiere, se prepara también la representación gráfica en formato GML y el Informe de Validación Gráfica Alternativa positivo, cuando proceda.",
        ],
      },
      {
        h2: "Formalización: Notaría, Registro y Catastro",
        paragraphs: [
      "La agrupación se formaliza normalmente mediante escritura ante Notaría y se inscribe en el Registro de la Propiedad. Después se actualiza la representación catastral para que el dibujo de Catastro coincida con la nueva realidad documentada.",
      "Nuestra parte es la técnica: la medición, los planos y la representación gráfica que esos trámites necesitan. La escritura y la inscripción corresponden a la Notaría y al Registro.",
        ],
      },
    ],
    relatedServices: ["agrupacion-fincas-canarias", "medicion-fincas-terrenos", "gml-catastro-registro"],
    relatedProblems: ["unir-varias-fincas"],
  },
  {
    slug: "primera-ocupacion-edificaciones-fuera-de-ordenacion",
    ogImage: "/og/guia-primera-ocupacion-edificaciones-fuera-de-ordenacion.jpg",
    title: "Primera ocupación en edificaciones fuera de ordenación",
    category: "Urbanismo",
    seoTitle: "Primera ocupación de edificaciones fuera de ordenación | MideFinca",
    metaDescription:
      "Qué documentación puede necesitarse para tramitar la primera ocupación de una edificación fuera de ordenación y por qué la resolución corresponde al Ayuntamiento.",
    excerpt: "Qué documentación puede necesitarse y por qué la resolución corresponde siempre al Ayuntamiento.",
    h1: "Primera ocupación en edificaciones fuera de ordenación: qué documentación puede necesitarse",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "Qué significa «fuera de ordenación»",
        paragraphs: [
      "Una edificación está fuera de ordenación cuando no se ajusta a la ordenación urbanística vigente, normalmente porque se construyó con una normativa anterior o sin ella. Esa situación no impide necesariamente usar el inmueble, pero condiciona qué trámites y qué intervenciones son posibles sobre él.",
        ],
      },
      {
        h2: "Qué es la primera ocupación en este contexto",
        paragraphs: [
      "La primera ocupación o utilización es el trámite por el que se acredita que una edificación reúne las condiciones exigibles para ser usada. Cuando la edificación está fuera de ordenación, el trámite solo puede plantearse si el régimen urbanístico del inmueble lo permite, algo que debe estudiarse individualmente antes de preparar nada.",
        ],
      },
      {
        h2: "Qué documentación técnica puede necesitarse",
        paragraphs: [
      "Dependiendo del municipio y del caso, el expediente puede requerir:",
        ],
        list: [
      "Informe técnico del estado actual de la edificación y de sus antecedentes.",
      "Medición y planos del estado actual.",
      "Certificado de antigüedad, para acreditar desde cuándo existe la construcción.",
      "Documentación de habitabilidad, en viviendas y cuando corresponda.",
        ],
      },
      {
        h2: "Quién resuelve el trámite",
        paragraphs: [
      "La primera ocupación la resuelve el Ayuntamiento competente conforme a la normativa urbanística. La documentación técnica bien preparada es la condición para que el expediente pueda tramitarse, pero ningún técnico puede garantizar la concesión: la decisión es municipal.",
        ],
      },
    ],
    relatedServices: ["primera-ocupacion-fuera-ordenacion", "certificado-antiguedad", "certificado-habitabilidad"],
    relatedProblems: ["primera-ocupacion-fuera-de-ordenacion"],
  },
  {
    slug: "que-es-uso-consolidado-canarias",
    ogImage: "/og/guia-que-es-uso-consolidado-canarias.jpg",
    title: "Qué es un uso consolidado y cómo se acredita en Canarias",
    category: "Urbanismo",
    seoTitle: "Qué es el uso consolidado y cómo se acredita en Canarias | MideFinca",
    metaDescription:
      "Qué significa que un uso está consolidado, qué evidencias lo demuestran y quién puede reconocerlo administrativamente en Canarias.",
    excerpt: "Qué significa que un uso está consolidado, qué evidencias lo demuestran y quién puede reconocerlo.",
    h1: "Qué es un uso consolidado y cómo se acredita en Canarias",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "Qué es un uso consolidado",
        paragraphs: [
      "En términos generales, se habla de uso consolidado cuando una edificación viene destinándose a un uso determinado de forma continuada desde una situación urbanística anterior, y la normativa puede llegar a reconocer esa situación. No es una etiqueta automática: su existencia debe estudiarse individualmente, caso por caso.",
        ],
      },
      {
        h2: "Qué evidencias sirven para acreditarlo",
        paragraphs: [
      "Acreditar un uso consolidado consiste en demostrar con evidencias qué uso tiene la edificación, desde cuándo y en qué condiciones. Las pruebas habituales son:",
        ],
        list: [
      "Fotografía aérea histórica y cartografía de distintas épocas.",
      "Documentación de época: contratos, recibos, licencias antiguas, suministros.",
      "La comprobación técnica del estado y del uso actual del inmueble.",
        ],
      },
      {
        h2: "Quién reconoce el uso consolidado",
        paragraphs: [
      "El reconocimiento administrativo del uso consolidado, cuando sea necesario, corresponde a la Administración competente conforme a la normativa urbanística aplicable. El trabajo técnico aporta la base objetiva: un informe que documenta las características, la antigüedad y las condiciones del uso existente con las evidencias analizadas.",
        ],
      },
    ],
    relatedServices: ["informe-uso-consolidado", "certificado-antiguedad"],
    relatedProblems: ["acreditar-uso-consolidado"],
  },
  {
    slug: "que-es-cedula-urbanistica",
    ogImage: "/og/guia-que-es-cedula-urbanistica.jpg",
    title: "Qué es una cédula urbanística y para qué sirve",
    category: "Urbanismo",
    seoTitle: "Qué es una cédula urbanística y para qué sirve | MideFinca",
    metaDescription:
      "La cédula urbanística es la información oficial sobre qué se puede hacer en una parcela: clasificación del suelo, usos y parámetros. Quién la expide y cuándo conviene pedirla.",
    excerpt: "El documento oficial que dice qué se puede hacer en una parcela: quién la expide y cuándo conviene pedirla.",
    h1: "Qué es una cédula urbanística y para qué sirve",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "La información urbanística oficial de una parcela",
        paragraphs: [
      "La cédula urbanística, o información urbanística, es el documento oficial que informa sobre las condiciones urbanísticas de una parcela o inmueble: clasificación y categorización del suelo, usos admitidos, parámetros de edificación y demás determinaciones aplicables del planeamiento municipal.",
        ],
      },
      {
        h2: "Para qué sirve en la práctica",
        paragraphs: ["Se suele necesitar en situaciones como estas:"],
        list: [
      "Antes de comprar una parcela, para saber qué se puede construir o qué usos admite.",
      "Antes de proyectar, para conocer los parámetros de partida.",
      "Cuando una notaría, un banco o una administración pide información urbanística oficial del inmueble.",
      "Para confirmar las condiciones de una parcela antes de iniciar cualquier trámite de edificación.",
        ],
      },
      {
        h2: "Quién la expide y quién la solicita",
        paragraphs: [
      "La cédula urbanística la expide el Ayuntamiento competente. El propietario puede solicitarla directamente o encargar la tramitación a un técnico, que identifica y documenta correctamente la parcela, prepara la solicitud y explica después el contenido de la respuesta municipal en términos claros.",
      "Conviene tener presente que la cédula informa de las condiciones urbanísticas, pero la viabilidad concreta de un proyecto se confirma con su proyecto técnico y, en su caso, con la licencia correspondiente.",
        ],
      },
    ],
    relatedServices: ["cedula-urbanistica", "alineaciones-rasantes", "licencia-obra-mayor"],
    relatedProblems: ["que-puedo-construir-en-parcela"],
  },
  {
    slug: "que-son-alineaciones-y-rasantes",
    ogImage: "/og/guia-que-son-alineaciones-y-rasantes.jpg",
    title: "Qué son las alineaciones y rasantes y por qué se necesitan antes de construir",
    category: "Urbanismo",
    seoTitle: "Qué son las alineaciones y rasantes | MideFinca",
    metaDescription:
      "La alineación marca la posición de la edificación respecto de la vía y la rasante fija su cota de referencia. Qué son, quién las expide y por qué se necesitan antes de construir.",
    excerpt: "La alineación marca la posición respecto de la vía y la rasante la cota de referencia: por eso se necesitan antes de construir.",
    h1: "Qué son las alineaciones y rasantes y por qué se necesitan antes de construir",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "Alineación: la posición de la edificación respecto de la vía",
        paragraphs: [
      "La alineación es la línea que marca hasta dónde puede llegar la edificación respecto de la vía pública, según el planeamiento. No es lo mismo que el lindero de la parcela: la alineación es una determinación urbanística y el lindero es el límite de la propiedad. Pueden coincidir o no.",
        ],
      },
      {
        h2: "Rasante: la cota de referencia para construir",
        paragraphs: [
      "La rasante es la cota oficial que sirve de referencia para la futura edificación: a qué altura queda el edificio respecto de la vía. Las cotas son, sencillamente, las alturas de cada punto medidas respecto a una referencia. Conocer la rasante con precisión evita errores de altura que después son caros o imposibles de corregir.",
        ],
      },
      {
        h2: "Por qué se necesitan antes de proyectar y construir",
        paragraphs: [
      "Proyectar sin conocer la alineación y la rasante oficiales es arriesgarse a que el proyecto no encaje con el planeamiento o a tener que rehacerlo durante la licencia. Por eso se solicitan al inicio, como datos de partida del proyecto.",
        ],
      },
      {
        h2: "Quién las expide y qué documentación técnica hace falta",
        paragraphs: [
      "Cuando el procedimiento lo requiere, las alineaciones y rasantes oficiales las emite el Ayuntamiento. Para solicitarlas o para definirlas técnicamente hace falta un levantamiento topográfico preciso de la parcela y de su entorno respecto de la vía, con sus planos y sus cotas. Esa documentación técnica y topográfica es la que preparamos desde MideFinca, junto con la presentación de la solicitud cuando proceda.",
        ],
      },
    ],
    relatedServices: ["alineaciones-rasantes", "levantamiento-topografico", "cedula-urbanistica"],
    relatedProblems: ["alineaciones-y-rasantes"],
  },
  {
    slug: "comunicacion-previa-obra-cuando-se-utiliza",
    ogImage: "/og/guia-comunicacion-previa-obra-cuando-se-utiliza.jpg",
    title: "Comunicación previa de obra: cuándo se utiliza y qué documentación necesitas",
    category: "Urbanismo",
    seoTitle: "Comunicación previa de obra: cuándo se utiliza | MideFinca",
    metaDescription:
      "Qué obras pueden tramitarse por comunicación previa, qué documentación técnica suelen necesitar y por qué depende de la ordenanza de cada municipio.",
    excerpt: "Qué obras pueden tramitarse por comunicación previa y por qué depende de la ordenanza de cada municipio.",
    h1: "Comunicación previa de obra: cuándo se utiliza y qué documentación necesitas",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "Qué es la comunicación previa",
        paragraphs: [
      "La comunicación previa es un procedimiento simplificado por el que se comunica al Ayuntamiento la realización de determinadas actuaciones, aportando la documentación que la ordenanza municipal exija. Es más ágil que una licencia, pero no está disponible para cualquier obra.",
        ],
      },
      {
        h2: "No toda obra pequeña es comunicación previa",
        paragraphs: [
      "Es un error frecuente dar por hecho que una «obra menor» se tramita siempre por comunicación previa. La calificación de cada actuación depende de la ordenanza de cada municipio y del tipo de obra: si afecta a estructura, fachada o distribución, entre otros factores. Lo correcto es comprobar qué procedimiento corresponde antes de presentar nada.",
        ],
      },
      {
        h2: "Qué documentación técnica suele necesitarse",
        paragraphs: ["Según el caso y la ordenanza, la comunicación puede requerir:"],
        list: [
      "Memoria descriptiva de la actuación.",
      "Planos o medición del estado actual y del previsto.",
      "Presupuesto de ejecución de la obra.",
      "Documentación del inmueble: referencia catastral y datos registrales básicos.",
        ],
      },
      {
        h2: "Y si la obra necesita licencia",
        paragraphs: [
      "Si la comprobación determina que la actuación exige licencia de obra, habrá que preparar el procedimiento correspondiente, incluido el proyecto técnico cuando la obra lo requiera. Saberlo desde el principio evita empezar por el trámite equivocado.",
        ],
      },
    ],
    relatedServices: ["comunicacion-previa-obra", "licencia-obra-mayor"],
    relatedProblems: ["que-permiso-necesito-reforma"],
  },
  {
    slug: "licencia-obra-mayor-documentacion",
    ogImage: "/og/guia-licencia-obra-mayor-documentacion.jpg",
    title: "Licencia de obra mayor: qué documentación necesitas",
    category: "Urbanismo",
    seoTitle: "Licencia de obra mayor: proyecto y documentación | MideFinca",
    metaDescription:
      "Qué actuaciones exigen licencia de obra mayor, qué documentación técnica necesita el expediente y quién concede la licencia en Canarias.",
    excerpt: "Qué actuaciones exigen licencia de obra mayor y qué documentación técnica necesita el expediente.",
    h1: "Licencia de obra mayor: proyecto, documentación y tramitación",
    updatedAt: "2026-08-23",
    sections: [
      {
        h2: "Qué actuaciones exigen licencia de obra mayor",
        paragraphs: [
      "Dependiendo del caso y de la ordenanza municipal, suelen necesitar licencia de obra mayor la obra nueva, las ampliaciones, determinadas reformas, las rehabilitaciones, las intervenciones estructurales y otras actuaciones sometidas a licencia. La comprobación del procedimiento que corresponde a cada actuación es el primer paso del trabajo.",
        ],
      },
      {
        h2: "Qué documentación técnica necesita el expediente",
        paragraphs: ["Una solicitud de licencia de obra mayor suele incluir:"],
        list: [
      "Proyecto técnico: memoria, planos y documentación exigible según la actuación.",
      "Levantamiento y medición del estado actual, cuando proceda.",
      "Presupuesto de ejecución material de la obra.",
      "Documentación del inmueble: referencia catastral, escritura y antecedentes.",
        ],
      },
      {
        h2: "Quién concede la licencia",
        paragraphs: [
      "La licencia la concede o la deniega el Ayuntamiento conforme a la normativa urbanística, y ningún técnico puede garantizar su concesión. Lo que sí depende de la parte técnica es que el expediente entre completo y bien planteado, sin errores que provoquen requerimientos de subsanación y retrasos.",
        ],
      },
      {
        h2: "Cuánto puede tardar",
        paragraphs: [
      "Los plazos dependen del municipio y del expediente concreto, y no es prudente prometer fechas. Lo recomendable es preparar la documentación con antelación suficiente y responder con rapidez a los requerimientos técnicos que puedan plantearse durante la tramitación.",
        ],
      },
    ],
    relatedServices: ["licencia-obra-mayor", "comunicacion-previa-obra", "cedula-urbanistica"],
    relatedProblems: ["obra-con-licencia-municipal"],
  },
  {
    slug: "que-es-division-horizontal",
    ogImage: "/og/guia-que-es-division-horizontal.jpg",
    title: "Qué es la división horizontal y cuándo se necesita",
    category: "Registro y escrituras",
    seoTitle: "Qué es la división horizontal y cuándo se necesita | MideFinca",
    metaDescription:
      "La división horizontal permite dividir jurídicamente un edificio en unidades independientes. Qué documenta, qué son las cuotas de participación y cuándo hace falta medir.",
    excerpt: "La figura que convierte un edificio en unidades independientes: qué documenta y cuándo hace falta medir.",
    h1: "Qué es la división horizontal y cuándo se necesita",
    updatedAt: "2026-08-24",
    sections: [
      {
        h2: "Un edificio, varias propiedades independientes",
        paragraphs: [
          "La división horizontal es la figura jurídica que permite dividir un edificio en unidades independientes, cada una con su propia titularidad: viviendas, locales, trasteros o plazas de garaje. Sobre esa base funciona la propiedad horizontal, el régimen habitual de los edificios residenciales con elementos comunes.",
          "Cada unidad queda descrita en el título constitutivo: su superficie, su distribución, los elementos comunes que le corresponden y su cuota de participación en el conjunto del edificio.",
        ],
      },
      {
        h2: "Cuándo se necesita trabajo técnico de medición",
        paragraphs: ["La parte técnica cobra importancia en situaciones como estas:"],
        list: [
          "Constituir la división horizontal de un edificio nuevo o existente que nunca se dividió.",
          "Modificar una división ya constituida: unir o separar unidades, cambiar superficies o usos.",
          "Corregir diferencias entre lo construido y lo descrito en el título.",
          "Aportar datos medidos para una compraventa, herencia o tasación de una unidad.",
        ],
      },
      {
        h2: "Por qué las cuotas y las superficies deben medirse",
        paragraphs: [
          "Las cuotas de participación suelen calcularse en relación con la superficie o el valor de cada unidad, y de ellas dependen los gastos comunitarios y las mayorías en las juntas. Si la superficie de partida no es la real, todo lo construido sobre ella queda desviado.",
          "La medición técnica previa, unidad por unidad, evita escrituras basadas en cifras heredadas de planos antiguos o de declaraciones aproximadas. La escritura y la inscripción corresponden a notario y Registro; la base de datos medida corresponde al técnico.",
        ],
      },
    ],
    relatedServices: ["division-horizontal-canarias", "certificado-superficies", "declaracion-obra-nueva"],
    relatedProblems: ["dividir-edificio-en-unidades"],
  },
  {
    slug: "cambio-de-uso-local-a-vivienda-canarias",
    ogImage: "/og/guia-cambio-de-uso-local-a-vivienda.jpg",
    title: "Cambio de uso de local a vivienda en Canarias: qué comprobar antes",
    category: "Urbanismo",
    seoTitle: "Cambio de uso de local a vivienda en Canarias: qué comprobar | MideFinca",
    metaDescription:
      "Antes de convertir un local en vivienda en Canarias hay que comprobar planeamiento, ordenanza municipal, condiciones del local y estatutos de la comunidad. Te explicamos el estudio de viabilidad.",
    excerpt: "Lo que hay que comprobar, antes de obrar o de comprar, para saber si un local puede convertirse en vivienda.",
    h1: "Cambio de uso de local a vivienda en Canarias: qué comprobar antes",
    updatedAt: "2026-08-24",
    sections: [
      {
        h2: "Primero la viabilidad, después la obra",
        paragraphs: [
          "Un cambio de uso de local a vivienda exige dos comprobaciones previas: que la normativa urbanística lo admita y que el espacio pueda cumplir las condiciones exigibles a una vivienda. Comprar u obrar sin comprobar ambas cosas es la causa más frecuente de proyectos bloqueados.",
          "El estudio de viabilidad responde a esa doble pregunta con datos: normativa aplicable y condiciones físicas medidas.",
        ],
      },
      {
        h2: "Qué condiciones se comprueban en el local",
        paragraphs: ["Las condiciones exactas dependen de la normativa aplicable a cada municipio, pero el estudio suele revisar:"],
        list: [
          "Huecos a espacio exterior o patio que permitan iluminación y ventilación natural de las estancias habitables.",
          "Altura libre y distribución, para comprobar si cabe un programa de vivienda conforme a la norma.",
          "Accesos, aseos y núcleos de instalaciones disponibles o posibles.",
          "Estatutos de la comunidad de propietarios, que pueden condicionar o limitar el cambio de uso.",
        ],
      },
      {
        h2: "Qué procedimiento puede aplicar",
        paragraphs: [
          "Según el municipio y el alcance de la actuación, el cambio de uso puede requerir desde una comunicación previa o declaración responsable hasta una licencia urbanística con proyecto técnico. La calificación y la resolución corresponden siempre al Ayuntamiento.",
          "Si el resultado del estudio es positivo, el siguiente paso es preparar la documentación técnica del procedimiento que corresponda. Si es negativo, te lo decimos antes de que inviertas en obra.",
        ],
      },
    ],
    relatedServices: ["cambio-de-uso-local-a-vivienda", "certificado-habitabilidad", "comunicacion-previa-obra"],
    relatedProblems: ["convertir-local-en-vivienda", "que-permiso-necesito-reforma"],
  },
  {
    slug: "que-es-ivga-informe-validacion-grafica",
    ogImage: "/og/guia-que-es-ivga.jpg",
    title: "Qué es el IVGA: el Informe de Validación Gráfica Alternativa",
    category: "Catastro",
    seoTitle: "Qué es el IVGA (Informe de Validación Gráfica Alternativa) | MideFinca",
    metaDescription:
      "El IVGA es el informe con el que un técnico competente valida la representación gráfica georreferenciada de una finca. Cuándo se utiliza en Catastro y qué significa un IVGA positivo.",
    excerpt: "El informe con el que un técnico valida gráficamente la representación de una finca: cuándo se usa y qué significa que sea positivo.",
    h1: "Qué es el IVGA: el Informe de Validación Gráfica Alternativa",
    updatedAt: "2026-08-24",
    sections: [
      {
        h2: "Una validación técnica de la geometría de la finca",
        paragraphs: [
          "El IVGA (Informe de Validación Gráfica Alternativa) es un documento técnico por el que un técnico competente valida la representación gráfica georreferenciada de una finca: confirma que la geometría medida (vértices, superficie y coordenadas) es coherente con la realidad del terreno.",
          "Se utiliza dentro de los procedimientos de Catastro como una alternativa técnica a la notificación individual a los titulares colindantes: en lugar de requerir su conformidad, la representación gráfica se respalda con la validación de un técnico.",
        ],
      },
      {
        h2: "Cuándo puede ser necesario",
        paragraphs: ["No todos los trámites gráficos requieren un IVGA. Según el caso, puede ser necesario o conveniente en situaciones como estas:"],
        list: [
          "Correcciones de superficie o de geometría en Catastro cuando los datos registrados difieren de la realidad medida.",
          "Aportación de representaciones gráficas (por ejemplo, archivos GML) en procedimientos de coordinación entre Catastro y el Registro de la Propiedad.",
          "Declaraciones o rectificaciones en las que Catastro requiera respaldo técnico de la geometría aportada.",
        ],
      },
      {
        h2: "Qué significa un IVGA positivo, y qué no significa",
        paragraphs: [
          "Un IVGA positivo significa que el técnico ha validado la representación gráfica aportada. Es un respaldo técnico importante, pero no es un título de propiedad, no legaliza por sí solo una construcción o una división y no sustituye la resolución del procedimiento.",
          "Quien tramita y resuelve es Catastro (o el organismo correspondiente). Nuestro trabajo es medir correctamente, preparar la documentación gráfica y, cuando proceda, emitir la validación técnica para que el expediente avance sobre datos sólidos.",
        ],
      },
      {
        h2: "Quién puede emitirlo",
        paragraphs: [
          "El IVGA lo emite un técnico competente (arquitectos, arquitectos técnicos, ingenieros y otros titulados habilitados por la normativa catastral), que asume la responsabilidad técnica de la validación. Por eso la base de todo el informe es una medición georreferenciada bien hecha sobre el terreno.",
        ],
      },
    ],
    relatedServices: ["gml-catastro-registro", "correccion-discrepancias-catastrales", "georreferenciacion-fincas"],
    relatedProblems: ["catastro-escritura-no-coinciden", "necesito-gml"],
  },
  {
    slug: "como-rectificar-superficie-catastro",
    ogImage: "/og/guia-como-rectificar-superficie-catastro.jpg",
    title: "Cómo rectificar la superficie de tu finca en Catastro",
    category: "Catastro",
    seoTitle: "Cómo rectificar la superficie de tu finca en Catastro | MideFinca",
    metaDescription:
      "Si Catastro registra menos (o más) metros de los reales, la superficie puede rectificarse aportando una medición georreferenciada. Qué revisar, qué documentar y cómo se plantea el procedimiento.",
    excerpt: "Qué revisar y qué documentar cuando los metros que figuran en Catastro no coinciden con tu finca real.",
    h1: "Cómo rectificar la superficie de tu finca en Catastro",
    updatedAt: "2026-08-24",
    sections: [
      {
        h2: "Cuando los metros de Catastro no son los reales",
        paragraphs: [
          "Catastro es una base de datos de fines principalmente fiscales, y su superficie no siempre coincide con la realidad ni con la escritura. La diferencia puede venir de mediciones antiguas, de cartografía desplazada, de parcelaciones históricas o de errores de transcripción.",
          "Antes de iniciar cualquier trámite hay que saber cuánto mide realmente la finca: sin una medición georreferenciada, cualquier reclamación se apoya solo en opiniones.",
        ],
      },
      {
        h2: "Qué conviene revisar antes de pedir nada",
        paragraphs: ["Antes de plantear una rectificación, conviene reunir y comprobar:"],
        list: [
          "La medición georreferenciada real de la finca, con sus vértices y su superficie.",
          "La superficie y los linderos descritos en la escritura o en el título de propiedad.",
          "La cartografía catastral actual de tu parcela y de las colindantes, para detectar solapes o huecos.",
          "Los posibles efectos de la nueva superficie (fiscales o registrales), que conviene valorar con tu asesor antes de mover ficha.",
        ],
      },
      {
        h2: "Cómo se plantea la rectificación",
        paragraphs: [
          "Con la medición hecha, se prepara la documentación gráfica de la finca (representación georreferenciada y, cuando proceda, el archivo GML) y la propuesta de corrección de superficie. Según el caso, Catastro puede requerir la notificación a los titulares colindantes o un respaldo técnico como el IVGA.",
          "Es importante tener claro que quien tramita y resuelve el procedimiento es Catastro. Nuestro trabajo es medir, documentar y fundamentar técnicamente la propuesta; la aceptación depende del expediente y no puede prometerse.",
        ],
      },
      {
        h2: "Qué cambia y qué no cambia",
        paragraphs: [
          "Rectificar la superficie en Catastro no modifica por sí solo el Registro de la Propiedad ni la escritura. Si la superficie registral también debe actualizarse, puede ser necesario un trámite adicional (por ejemplo, un exceso de cabida u otro procedimiento), que se valora caso por caso.",
          "Por eso conviene plantear el trabajo con una visión completa: medición, Catastro y, si fuera necesario, la coordinación con el Registro a través de notario.",
        ],
      },
    ],
    relatedServices: ["correccion-discrepancias-catastrales", "medicion-fincas-terrenos", "gml-catastro-registro"],
    relatedProblems: ["catastro-escritura-no-coinciden", "mi-finca-tiene-mas-o-menos-metros"],
  },
];

export const upcomingGuides = [
  { title: "¿Cuánto cuesta medir una finca?", category: "Medición · cubierta en la página de precios", href: "/precios/" },
];

export const getGuide = (slug) => guides.find((g) => g.slug === slug);
